import { useState } from 'react';
import { ChevronDown, ChevronRight, Layers } from 'lucide-react';

interface ServiceDefinition {
  id: string;
  name: string;
}

interface LinkedServiceType {
  id: string;
  selectedServiceDefinitionIds: string[];
  serviceType: {
    label: string;
    serviceDefinitions: ServiceDefinition[];
  };
}

interface Props {
  linkedServiceTypes: LinkedServiceType[];
}

export default function ServiceTypesReadOnlyView({ linkedServiceTypes }: Props) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (linkedServiceTypes.length === 0) {
    return (
      <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
        <Layers className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500 text-sm">No service categories configured.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {linkedServiceTypes.map(item => {
        const isExpanded = expandedIds.has(item.id);
        const selected = item.selectedServiceDefinitionIds;
        const total = item.serviceType.serviceDefinitions.length;

        return (
          <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleExpand(item.id)}
              className="w-full flex items-center gap-2 px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
            >
              {isExpanded
                ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              }
              <span className="font-medium text-gray-900">{item.serviceType.label}</span>
              <span className="text-xs text-gray-400 ml-1">{selected.length} / {total} selected</span>
            </button>

            {isExpanded && (
              <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                {item.serviceType.serviceDefinitions.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No active service names.</p>
                ) : (
                  <div className="space-y-2">
                    {item.serviceType.serviceDefinitions.map(sd => (
                      <div key={sd.id} className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                          selected.includes(sd.id)
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-gray-300 bg-white'
                        }`}>
                          {selected.includes(sd.id) && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                              <path d="M1.5 5L3.5 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <span className={`text-sm ${selected.includes(sd.id) ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                          {sd.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}



